import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../api';
import { logger } from '../utils/logger';

/**
 * Publicly-editable site settings (contact details, social links, …) are managed
 * from the portal CMS → Site Settings and stored in the `site_settings` table.
 * The public endpoint `GET /cms/settings` returns only rows flagged `is_public`.
 *
 * This provider fetches them once on app load and exposes a `get(key, fallback)`
 * helper. Every consumer passes the current known-good value as its fallback, so
 * if the API is unreachable or a setting is blank the site renders exactly as it
 * did before — the CMS value simply takes over when present.
 */

// Current live values — used as fallbacks so a failed fetch never regresses the UI.
export const SETTING_DEFAULTS: Record<string, string> = {
  contact_email:    'admin@surerightstay.com',
  contact_phone:    '+234 8135138935',
  contact_whatsapp: '+234 816 307 4798',
  contact_whatsapp_e164: '2348163074798',
  social_facebook:  'https://www.facebook.com/people/Surerightstay/61587016990179/',
  social_twitter:   'https://x.com/SureRightStay',
  social_instagram: 'https://www.instagram.com/surerightstay/',
  // Intentionally blank by default — the old hard-coded link pointed at the
  // theme author (shreethemes). Social icons hide themselves when blank, so
  // LinkedIn stays hidden until an admin sets a real URL in the CMS.
  social_linkedin:  '',
  social_youtube:   '',
};

interface SiteSettingsContextValue {
  /** Returns the CMS value for `key` when present & non-empty, else `fallback`. */
  get: (key: string, fallback?: string) => string;
  loading: boolean;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
  get: (_key, fallback = '') => fallback,
  loading: false,
});

// Shape of `GET /cms/settings`: { success, data: { group: [{ key, value }, …] } }
interface RawSetting { key: string; value: string | null; }
interface SettingsResponse { data?: Record<string, RawSetting[]> }

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiGet<SettingsResponse>('/cms/settings')
      .then((res) => {
        if (cancelled) return;
        const flat: Record<string, string> = {};
        Object.values(res?.data ?? {}).forEach((group) => {
          (group ?? []).forEach((s) => {
            if (s?.key) flat[s.key] = s.value ?? '';
          });
        });
        setValues(flat);
      })
      .catch((err) => {
        // Non-fatal: components fall back to their default values.
        logger.error('Failed to load site settings', err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo<SiteSettingsContextValue>(() => ({
    loading,
    get: (key, fallback) => {
      const v = values[key];
      if (typeof v === 'string' && v.trim() !== '') return v;
      return fallback ?? SETTING_DEFAULTS[key] ?? '';
    },
  }), [values, loading]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

/** Strips spaces/formatting for use in a `tel:` href. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
